import React, { useState, useContext, useCallback } from "react";
import PropTypes from "prop-types";
import { IconButton, Box, Tooltip } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import NodesDynamicForm from "../../../../forms/NodesDynamicForm";
import nodeTypesConfig from "../../../../models/nodeTypesConfig";
import AddNodeContext from "../../../../contexts/AddNodeContext";
import NodeConfigModal from "../../menus/NodeConfigModal";
import CustomHandle from "../../handles/CustomHandle";
import useAutomations from "../../../../hooks/automationhooks/useAutomations";
import { useThemeContext } from "../../../../Theme/Theme";
import { useReactFlow } from "@xyflow/react";

const TriggerNodeTemplate = ({ id, type, data, selected, children }) => {
  const [configModal, setConfigModal] = useState({ open: false });
  const { deleteElements } = useReactFlow();
  const { editNode, onNodesDelete, setSelectedNode } =
    useContext(AddNodeContext);
  const { mode } = useThemeContext();
  const { brandSegments } = useAutomations();

  const handleDelete = useCallback(
    (e) => {
      e.stopPropagation();
      onNodesDelete([{ id }]);
    },
    [id, onNodesDelete]
  );

  const handleEdit = useCallback(
    (e) => {
      e.stopPropagation();

      if (setSelectedNode) {
        setSelectedNode({ id, type, data, selected });
      }
    },
    [id, type, data, selected, setSelectedNode]
  );

  const handleContentClick = useCallback((e) => {
    e.stopPropagation();
  }, []);

  // Get the icon component from config
  const nodeConfig = nodeTypesConfig.triggers.find(
    (node) => node.type === type
  );
  const IconComponent = nodeConfig?.icon;
  const iconColor = nodeConfig?.iconColor || "#e0e0e0"; // Default gray if no color specified

  return (
    <>
      <div
        className={`node-template ${mode}`}
        style={{
          minWidth: 150,
          maxWidth: 150,
          width: "100%",
          minHeight: 50,
          maxHeight: 50,
          height: "100%",
          borderRadius: 10,
        }}
        onClick={handleContentClick}
      >
        <Box
          sx={{
            marginLeft: -1,
            marginRight: -1,
          }}
        >
          {/* Optional top header */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              borderBottom: "1px solid #444",
              width: "100%",
              marginTop: -20,
            }}
          >
            <strong style={{ color: "#00aaff", fontSize: 8, marginTop: 5 }}>
              {data.label}
            </strong>
            <div style={{ display: "flex", gap: 1 }}>
              <Tooltip title="Edit" placement="top">
                <IconButton size="small" onClick={handleEdit}>
                  <EditIcon sx={{ color: "#aaa", fontSize: 10 }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete" placement="top">
                <IconButton size="small" onClick={handleDelete}>
                  <DeleteIcon sx={{ color: "#aaa", fontSize: 10 }} />
                </IconButton>
              </Tooltip>
            </div>
          </div>

          {/* Flexbox container for children and icon */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            {/* Children */}
            <div
              style={{
                marginTop: -5,
                flex: 1,
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {children}
            </div>

            {/* Icon */}
            {IconComponent && (
              <Box
                sx={{
                  backgroundColor: iconColor,
                  borderRadius: "50%",
                  width: 18, // Fixed size for the container
                  height: 18,
                  display: "flex",
                  alignItems: "center", // Centers vertically
                  justifyContent: "center", // Centers horizontally
                  flexShrink: 0,
                  mt: 0.1,
                  color: "white",
                  position: "relative", // Ensure inner icon respects container boundaries
                  lineHeight: 0, // Remove font-based vertical misalignment
                }}
              >
                <IconComponent
                  sx={{ width: "65%", height: "75%" }}
                  width={13}
                  height={13}
                  mb={0.1}
                />{" "}
                {/* Scales icon relative to the container */}
              </Box>
            )}
          </div>
        </Box>

        {/* Handles */}
        <CustomHandle
          type="source"
          position="bottom"
          style={{ background: "#00aaff" }}
        />
      </div>

      {configModal.open && (
        <NodeConfigModal
          open={configModal.open}
          onClose={() => setConfigModal({ open: false })}
        >
          {configModal.content}
        </NodeConfigModal>
      )}
    </>
  );
};

TriggerNodeTemplate.propTypes = {
  id: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired,
  data: PropTypes.object.isRequired,
  selected: PropTypes.bool,
  children: PropTypes.node,
};

export default TriggerNodeTemplate;
